import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'modules/api/utils';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';

import {
  type TagServiceCategoriesResponse,
  type TagServiceUserTagsResponse,
  mapCategoriesFromTagService,
  mapCategoriesToTagService,
  mapUserTagsFromTagService,
  mapUserTagsToTagService,
} from './tagServiceMappers';
import type { TagCategory, TagSelections } from './types';

const getBaseURL = () => {
  const url = configService.getUrl(UrlNames.TagApi);
  if (!url) throw new Error('VITE_APP_TAG_API_URL is not configured');
  return url;
};

export const tagServiceApi = createApi({
  reducerPath: 'tagServiceApi',
  baseQuery: retry(axiosBaseQuery({ baseURL: getBaseURL }), { maxRetries: 0 }),
  tagTypes: ['EVENT_TAG_CATEGORIES', 'MY_EVENT_TAGS'],
  endpoints: (builder) => ({
    setEventTagCategories: builder.mutation<void, { eventId: number; categories: TagCategory[] }>({
      query: ({ eventId, categories }) => ({
        url: '/v1/tag-categories',
        params: { eventId: String(eventId) },
        method: 'PUT',
        data: mapCategoriesToTagService(categories),
      }),
      invalidatesTags: (_result, _error, { eventId }) => [{ type: 'EVENT_TAG_CATEGORIES', id: eventId }],
    }),

    getEventTagCategories: builder.query<TagCategory[], { eventId: number }>({
      query: ({ eventId }) => ({
        url: '/v1/tag-categories',
        params: { eventId: String(eventId) },
      }),
      transformResponse: mapCategoriesFromTagService,
      providesTags: (_result, _error, { eventId }) => [{ type: 'EVENT_TAG_CATEGORIES', id: eventId }],
    }),

    getMyEventTags: builder.query<TagSelections, { eventId: number; accountId: number }>({
      query: ({ eventId, accountId }) => ({
        url: `/v1/tags/user/${accountId}`,
        params: { eventId: String(eventId) },
      }),
      transformResponse: mapUserTagsFromTagService,
      providesTags: (_result, _error, { eventId }) => [{ type: 'MY_EVENT_TAGS', id: eventId }],
    }),

    setMyEventTags: builder.mutation<void, { eventId: number; accountId: number; selections: TagSelections }>({
      query: ({ eventId, accountId, selections }) => ({
        url: `/v1/tags/user/${accountId}`,
        params: { eventId: String(eventId) },
        method: 'PUT',
        data: mapUserTagsToTagService(selections),
      }),
      invalidatesTags: (_result, _error, { eventId }) => [{ type: 'MY_EVENT_TAGS', id: eventId }],
    }),
  }),
});
